import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  IPartAddtoOrders,
  IPartAddtoOrdersInitialState,
  IFindAllPartAddtoOrdersResponse,
} from "./partAddtoOrdersModel";
import {
  createPartAddtoOrders,
  updatePartAddtoOrders,
  deletePartAddtoOrders,
  getPartAddtoOrdersById,
  findAllPartAddtoOrders,
  findAllActivePartAddtoOrders,
  activeInActivePartAddtoOrders,
} from "./services/ghat.services";

const initialState: IPartAddtoOrdersInitialState = {
  loading: false,
  error: "",
  PartAddtoOrdersID: null,
  PartAddtoOrdersList: [],
  PartAddtoOrdersDetail: {
    ID: undefined,
    No: undefined,
    Date: new Date(),
    process_id: 0,
    OrderNo: 0,
    ProductName: "",
    Weight: 0,
    Remarks: "",
    IsActive: true,
    CreatedBy: "",
    UpdatedBy: "",
    RAWITEM: [],
    ORDERITEM: [],
  },
  PartAddtoOrdersOrderItemIndex: -1,
  PartAddtoOrdersRawItemIndex: -1,
  PartAddtoOrdersSelectOrderDialog: false,
  PartAddtoOrdersRawItemDeleteConfirmDialog: false,
  PartAddtoOrdersOrderItemDeleteConfirmDialog: false,
  PartAddtoOrdersSave: false,
  FilterPartAddtoOrders: {
    ProductName: "",
    OrderNo: null,
    process_id: null,
    fromDate: "",
    toDate: "",
  },
  PartAddtoOrdersPreviousOrder: null,
};

const partAddtoOrdersSlice = createSlice({
  name: "partAddtoOrders",
  initialState,
  reducers: {
    clearPartAddtoOrdersDetail: (state) => {
      state.PartAddtoOrdersDetail = initialState.PartAddtoOrdersDetail;
    },
    setPartAddtoOrdersID: (state, action: PayloadAction<number>) => {
      state.PartAddtoOrdersID = action.payload;
    },
    setFilterPartAddtoOrders: (
      state,
      action: PayloadAction<Partial<typeof initialState.FilterPartAddtoOrders>>,
    ) => {
      state.FilterPartAddtoOrders = {
        ...state.FilterPartAddtoOrders,
        ...action.payload,
      };
    },
    setPartAddtoOrdersPreviousOrder: (
      state,
      action: PayloadAction<IPartAddtoOrders>,
    ) => {
      state.PartAddtoOrdersPreviousOrder = action.payload;
    },
    clearFilterPartAddtoOrders: (state) => {
      state.FilterPartAddtoOrders = initialState.FilterPartAddtoOrders;
    },
    setPartAddtoOrdersSave: (state, action: PayloadAction<boolean>) => {
      state.PartAddtoOrdersSave = action.payload;
    },
    setPartAddtoOrdersOrderItemIndex: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.PartAddtoOrdersOrderItemIndex = action.payload;
    },
    setPartAddtoOrdersRawItemIndex: (state, action: PayloadAction<number>) => {
      state.PartAddtoOrdersRawItemIndex = action.payload;
    },
    openPartAddtoOrdersSelectOrderDialog: (state) => {
      state.PartAddtoOrdersSelectOrderDialog = true;
    },
    closePartAddtoOrdersSelectOrderDialog: (state) => {
      state.PartAddtoOrdersSelectOrderDialog = false;
    },
    openPartAddtoOrdersRawItemDeleteConfirmDialog: (state) => {
      state.PartAddtoOrdersRawItemDeleteConfirmDialog = true;
    },
    closePartAddtoOrdersRawItemDeleteConfirmDialog: (state) => {
      state.PartAddtoOrdersRawItemDeleteConfirmDialog = false;
    },
    openPartAddtoOrdersOrderItemDeleteConfirmDialog: (state) => {
      state.PartAddtoOrdersOrderItemDeleteConfirmDialog = true;
    },
    closePartAddtoOrdersOrderItemDeleteConfirmDialog: (state) => {
      state.PartAddtoOrdersOrderItemDeleteConfirmDialog = false;
    },
  },
  extraReducers: (builder) => {
    // Find All PartAddtoOrders
    builder.addCase(findAllPartAddtoOrders.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      findAllPartAddtoOrders.fulfilled,
      (state, action: PayloadAction<IFindAllPartAddtoOrdersResponse>) => {
        state.loading = false;
        state.PartAddtoOrdersList = action.payload.Data || [];
      },
    );
    builder.addCase(findAllPartAddtoOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.PartAddtoOrdersList = [];
    });

    // Find All Active PartAddtoOrders
    builder.addCase(findAllActivePartAddtoOrders.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      findAllActivePartAddtoOrders.fulfilled,
      (state, action: PayloadAction<IPartAddtoOrders[]>) => {
        state.loading = false;
        state.PartAddtoOrdersList = action.payload || [];
      },
    );
    builder.addCase(findAllActivePartAddtoOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.PartAddtoOrdersList = [];
    });

    // Create PartAddtoOrders
    builder.addCase(createPartAddtoOrders.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(createPartAddtoOrders.fulfilled, (state) => {
      state.loading = false;
      state.PartAddtoOrdersSave = false;
    });
    builder.addCase(createPartAddtoOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Update PartAddtoOrders
    builder.addCase(updatePartAddtoOrders.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(updatePartAddtoOrders.fulfilled, (state) => {
      state.loading = false;
      state.PartAddtoOrdersSave = false;
    });
    builder.addCase(updatePartAddtoOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Delete PartAddtoOrders
    builder.addCase(deletePartAddtoOrders.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deletePartAddtoOrders.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deletePartAddtoOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Get PartAddtoOrders By ID
    builder.addCase(getPartAddtoOrdersById.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getPartAddtoOrdersById.fulfilled,
      (state, action: PayloadAction<IPartAddtoOrders>) => {
        state.loading = false;
        state.PartAddtoOrdersDetail = action.payload;
      },
    );
    builder.addCase(getPartAddtoOrdersById.rejected, (state, action) => {
      state.loading = false;
      state.PartAddtoOrdersDetail = initialState.PartAddtoOrdersDetail;
      state.error = action.error.message || "Something went wrong";
    });

    // Active/InActive PartAddtoOrders
    builder.addCase(activeInActivePartAddtoOrders.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(activeInActivePartAddtoOrders.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(activeInActivePartAddtoOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  clearPartAddtoOrdersDetail,
  clearFilterPartAddtoOrders,
  closePartAddtoOrdersSelectOrderDialog,
  openPartAddtoOrdersSelectOrderDialog,
  setFilterPartAddtoOrders,
  setPartAddtoOrdersID,
  setPartAddtoOrdersOrderItemIndex,
  setPartAddtoOrdersRawItemIndex,
  setPartAddtoOrdersPreviousOrder,
  setPartAddtoOrdersSave,
  openPartAddtoOrdersRawItemDeleteConfirmDialog,
  closePartAddtoOrdersRawItemDeleteConfirmDialog,
  openPartAddtoOrdersOrderItemDeleteConfirmDialog,
  closePartAddtoOrdersOrderItemDeleteConfirmDialog,
} = partAddtoOrdersSlice.actions;

export default partAddtoOrdersSlice.reducer;
